import React, { useState, useEffect } from 'react';
import { MapPinIcon, SparklesIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { getPoolRoles, getJobCandidates, requestIntro } from '../services/api';
import { track, EVENTS } from '../services/analytics';

/* ----------------------------------------------------------------------------
 * Role Matches — the recruiter-side matching view. Pick a role and see pooled
 * candidates ranked by match_score = fit × intent × freshness. Then request a
 * double opt-in warm intro. PII-safe (names masked until the candidate accepts).
 * -------------------------------------------------------------------------- */

const scoreColor = (s) =>
  s >= 60 ? 'bg-green-100 text-green-800 ring-green-600/20'
  : s >= 35 ? 'bg-blue-100 text-blue-800 ring-blue-600/20'
  : s >= 15 ? 'bg-amber-100 text-amber-800 ring-amber-600/20'
  : 'bg-gray-100 text-gray-600 ring-gray-500/20';

function RoleMatches() {
  const [roles, setRoles] = useState([]);
  const [jobId, setJobId] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPoolRoles()
      .then((r) => {
        const list = r.data.roles || [];
        setRoles(list);
        if (list[0]) setJobId(list[0].id);
      })
      .catch(() => toast.error('Could not load roles'));
  }, []);

  useEffect(() => {
    if (!jobId) return;
    setLoading(true);
    getJobCandidates(jobId)
      .then((r) => { setCandidates(r.data.candidates || []); track(EVENTS.AGENT_MATCH, { count: r.data.candidates?.length || 0 }); })
      .catch(() => toast.error('Could not load candidates'))
      .finally(() => setLoading(false));
  }, [jobId]);

  const handleIntro = async (id) => {
    try {
      await requestIntro(id, jobId, "I'd like to connect about this role.");
      toast.success('Intro requested — the candidate will be asked to accept');
    } catch (e) {
      if (e.response?.status === 429) toast.error(e.response.data?.message || 'Daily intro limit reached');
      else toast.error(e.response?.data?.error || 'Could not request intro');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-1 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.08em] text-gray-500">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
          Recruiter · Role Matches
        </div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-gray-900">
          Best candidates for your role
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Ranked by fit × live intent × freshness — the people most likely to say yes.
        </p>

        {/* Role picker */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <label className="mb-1 block text-xs font-medium text-gray-600">Role</label>
          <div className="relative">
            <BriefcaseIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <select
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {roles.length === 0 && <option value="">No open roles yet</option>}
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.title} · {r.company}{r.location ? ` · ${r.location}` : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Ranked candidates */}
        <div className="mt-6">
          {loading ? (
            <div className="py-16 text-center text-sm text-gray-500">Ranking candidates…</div>
          ) : candidates.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 text-center">
              <SparklesIcon className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-700">No matching candidates yet</p>
              <p className="mt-1 text-sm text-gray-500">Grow the pool via the free candidate agent, then come back.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {candidates.map((c) => (
                <div key={c.id} className="flex gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md">
                  <div className="flex flex-col items-center justify-center">
                    <div className={`flex h-14 w-14 items-center justify-center rounded-xl text-lg font-bold ring-1 ${scoreColor(c.match_score)}`}>
                      {c.match_score}
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">match</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold text-gray-900">{c.display_name}</h3>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                      {c.location && <span className="inline-flex items-center gap-1"><MapPinIcon className="h-3.5 w-3.5" />{c.location}</span>}
                      {c.target_seniority && <span className="capitalize">{c.target_seniority}</span>}
                      <span>fit {c.fit_score}</span>
                      <span>intent {c.intent_score}</span>
                    </div>
                    {Array.isArray(c.reasons) && c.reasons.length > 0 && (
                      <p className="mt-1.5 truncate text-xs text-gray-600">{c.reasons.join(' · ')}</p>
                    )}
                    {Array.isArray(c.skills) && c.skills.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.skills.slice(0, 6).map((s, i) => (
                          <span key={i} className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{s}</span>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => handleIntro(c.id)}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                      title="Double opt-in: the candidate accepts before any intro"
                    >
                      Request intro
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoleMatches;
